import { GraphQLResult } from '@aws-amplify/api-graphql';
import { Signer } from '@aws-amplify/core';
import { Auth } from 'aws-amplify';
import { reportCdkRefusal } from 'app/services/session/cdkRefusalSignal';
import { captureException } from '@sentry/react';

import { fetchWithTransportRetry, isNetworkTransportError } from './networkTransport';

import {
  CreateAgaveExpenseAPInvoiceInput,
  ERPPostingStatus,
  ExportDateRangeEnum,
  FlexCodeTransactionStatus,
  FlexField,
  FlexFieldUpload,
  FlexFieldValue,
  GetProcoreProjectsPayloadInput,
  GetProcoreWBSCodesPayloadInput,
  HighnoteTransactionEvent,
  ReturnedStatus,
  WorkflowStatus,
} from 'API';
import type {
  GetProjectGroupInput,
  GetProjectGroupPayload,
  V2TransactionFilterOptionsPayload,
  V2CreateProjectGroupInput,
  V2UpdateProjectGroupAdminsInput,
  V2UpdateProjectGroupInput,
  // Aliased: `ProjectGroup` and `PaidolUserToProjectGroup` also exist in the Amplify
  // `API.ts`, and this module imports from both. The CDK versions are slim mirrors —
  // no `@hasMany` connections — so they are not interchangeable.
  ProjectGroup as CdkProjectGroup,
  PaidolUserToProjectGroup as CdkPaidolUserToProjectGroup,
  // SEE-301 Phase B. `SearchFlexCodingInput` collides with the Amplify type of the same
  // name, so it is aliased like the two above. The payload types do not collide.
  SearchFlexCodingInput as CdkSearchFlexCodingInput,
  SearchFlexCodesPayload,
  SearchFlexCostCodesPayload,
  SearchFlexCostTypesPayload,
  SearchFlexGlCodePayload,
  SearchFlexVendorsPayload,
  SearchSpxTransactionsInput,
  SearchSpxTransactionsPayload,
  // SEE-1233: all five names also exist in `API.ts`, whose Flex types the slice and pages
  // are typed against, so they are aliased like the ones above. The CDK types back the
  // field-name checks next to the Flex documents, and type the Flex variables and inputs.
  CreateFlexFieldInput as CdkCreateFlexFieldInput,
  CreateFlexFieldUploadInput as CdkCreateFlexFieldUploadInput,
  FlexField as CdkFlexField,
  FlexFieldUpload as CdkFlexFieldUpload,
  FlexFieldValue as CdkFlexFieldValue,
  QueryFlexFieldsByPaidolIdArgs,
  QueryFlexFieldValuesByFlexFieldIdArgs,
} from 'CDK_API';
// SEE-301 Phase B: reused rather than inlined, unlike the five flex documents. Those
// came from the generated `graphql/queries`, which CLAUDE.md bans importing; this one
// is hand-written and is already the live document for all three callers. Copying its
// ~250 lines here would mean two selection sets to keep field-for-field in sync with
// the CDK payload type, which is exactly the drift the CDK types were built off.
import { SearchHighnoteTransactions } from 'app/pages/store/queries';

// SEE-1232 rows i/j — HighnoteTransactionEvent reads. CDK serves a FLAT
// V2HighnoteTransactionEventRead, so only the slim documents move here:
// - posting status. Source: graphql_custom/queries.js (getHighnoteTransactionEventPostingStatus).
// - the by-paidol existence check. Source: hooks/useHasTransactions.ts (hasTransactionsQuery).
// - the first event of a transaction. Source: graphql_custom/transactions/queries.ts.
// `getHighnoteTransactionEventForReview` (~30 nested connections) is NOT served by CDK and
// stays on Amplify — SEE-1733.
const getHighnoteTransactionEventPostingStatusQuery = /* GraphQL */ `
  query GetHighnoteTransactionEvent($id: ID!) {
    getHighnoteTransactionEvent(id: $id) {
      id
      erpPostingStatus
      erpPostingError
      erpPostingWarning
      erpApprovalStatus
      agaveExpenseId
    }
  }
`;

const listHighnoteTransactionEventsByPaidolIdQuery = /* GraphQL */ `
  query HasTransactionsByPaidolId($paidolId: ID!, $limit: Int, $nextToken: String) {
    listHighnoteTransactionEventsByPaidolId(paidolId: $paidolId, limit: $limit, nextToken: $nextToken) {
      items {
        id
      }
      nextToken
    }
  }
`;

const listHighnoteTransactionEventsByTransactionIdQuery = /* GraphQL */ `
  query ListTransactionEventsByTransactionId(
    $transactionId: ID!
    $sortDirection: SortDirection
    $limit: Int
  ) {
    listHighnoteTransactionEventsByTransactionId(
      transactionId: $transactionId
      sortDirection: $sortDirection
      limit: $limit
    ) {
      items {
        id
        eventDate
        status
      }
      nextToken
    }
  }
`;

// SEE-1233 rows a–d — Flex Builder (Settings → Data fields). The selection sets are copied
// unchanged from graphql_custom/flex-fields.ts, which stays as the revert path. Only the
// variable declarations differ: `SortDirection` replaces `ModelSortDirection`, and callers
// no longer send `authPaidolId` or `status`, which the server now derives.
// Source: speedchain-cdk graphql/05-v2-transaction.graphql (SEE-1233)
const flexFieldsByPaidolIdQuery = /* GraphQL */ `
  query FlexFieldsByPaidolId($paidolId: ID!, $sortDirection: SortDirection, $limit: Int, $nextToken: String) {
    flexFieldsByPaidolId(
      paidolId: $paidolId
      sortDirection: $sortDirection
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        paidolId
        name
        alias
        order
        source
        createdBy
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

// Source: speedchain-cdk graphql/05-v2-transaction.graphql (SEE-1233)
const flexFieldValuesByFlexFieldIdQuery = /* GraphQL */ `
  query FlexFieldValuesByFlexFieldId(
    $flexFieldId: ID!
    $sortDirection: SortDirection
    $limit: Int
    $nextToken: String
  ) {
    flexFieldValuesByFlexFieldId(
      flexFieldId: $flexFieldId
      sortDirection: $sortDirection
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        paidolId
        flexFieldId
        externalId
        value
        status
        flexFieldUploadId
        createdAt
      }
      nextToken
    }
  }
`;

// Source: speedchain-cdk graphql/05-v2-transaction.graphql (SEE-1233)
const createFlexFieldMutation = /* GraphQL */ `
  mutation CreateFlexField($input: CreateFlexFieldInput!) {
    createFlexField(input: $input) {
      id
      paidolId
      name
      alias
      order
      source
      createdBy
      createdAt
      updatedAt
    }
  }
`;

// Source: speedchain-cdk graphql/05-v2-transaction.graphql (SEE-1233)
const createFlexFieldUploadMutation = /* GraphQL */ `
  mutation CreateFlexFieldUpload($input: CreateFlexFieldUploadInput!) {
    createFlexFieldUpload(input: $input) {
      id
      paidolId
      flexFieldId
      fileName
      status
      createdBy
      createdAt
    }
  }
`;

// SEE-1233: TypeScript 4.7 has no `satisfies`, so these lists pin, at compile time, that
// every field the Flex documents above select exists on the CDK type. The service tests
// pin each document's selection set to its list. Full assignability to the `API.ts` types
// can't hold: `codegen-cdk.yml` sets `skipTypename` and `enumsAsTypes`.
export const FLEX_FIELD_SELECTION: ReadonlyArray<keyof CdkFlexField> = [
  'id',
  'paidolId',
  'name',
  'alias',
  'order',
  'source',
  'createdBy',
  'createdAt',
  'updatedAt',
];
export const FLEX_FIELD_VALUE_SELECTION: ReadonlyArray<keyof CdkFlexFieldValue> = [
  'id',
  'paidolId',
  'flexFieldId',
  'externalId',
  'value',
  'status',
  'flexFieldUploadId',
  'createdAt',
];
export const FLEX_FIELD_UPLOAD_SELECTION: ReadonlyArray<keyof CdkFlexFieldUpload> = [
  'id',
  'paidolId',
  'flexFieldId',
  'fileName',
  'status',
  'createdBy',
  'createdAt',
];

export interface TransactionEventPostingStatus {
  id: string;
  erpPostingStatus?: string | null;
  erpPostingError?: string | null;
  erpPostingWarning?: string | null;
  erpApprovalStatus?: boolean | null;
  agaveExpenseId?: string | null;
}

export interface TransactionEventsPage<T> {
  items: T[];
  nextToken: string | null;
}

type AuthMode = 'cognito' | 'iam';

const updateTransactionMutation = /* GraphQL */ `
  mutation UpdateTransaction($input: UpdateTransactionInput!) {
    updateTransaction(input: $input) {
      statusCode
      body
    }
  }
`;

const returnTransactionToCardholderMutation = /* GraphQL */ `
  mutation ReturnTransactionToCardholder($input: ReturnTransactionToCardholderInput!) {
    returnTransactionToCardholder(input: $input) {
      statusCode
      body
    }
  }
`;

// SEE-1874: return every split of a split transaction with one message.
const returnAllSplitsMutation = /* GraphQL */ `
  mutation ReturnAllSplits($input: ReturnTransactionToCardholderInput!) {
    returnAllSplits(input: $input) {
      statusCode
      body
    }
  }
`;

// SEE-1604: Submit all splits. Served by the v2-transaction Lambda.
const submitAllSplitsMutation = /* GraphQL */ `
  mutation SubmitAllSplits($input: SubmitAllSplitsInput!) {
    submitAllSplits(input: $input) {
      statusCode
      body
    }
  }
`;

const listTransactionReviewMessagesQuery = /* GraphQL */ `
  query ListTransactionReviewMessages($input: ListTransactionReviewMessagesInput!) {
    listTransactionReviewMessages(input: $input) {
      statusCode
      body
    }
  }
`;

const getTransactionWaitingOnQuery = /* GraphQL */ `
  query GetTransactionWaitingOn($input: GetTransactionWaitingOnInput!) {
    getTransactionWaitingOn(input: $input) {
      statusCode
      body
    }
  }
`;

const enqueueTransactionsDownloadMutation = /* GraphQL */ `
  mutation EnqueueTransactionsDownload($input: EnqueueTransactionsDownloadInput!) {
    enqueueTransactionsDownload(input: $input) {
      statusCode
      body
    }
  }
`;

const markTransactionsExportedMutation = /* GraphQL */ `
  mutation MarkTransactionsExported($input: MarkTransactionsExportedInput!) {
    markTransactionsExported(input: $input) {
      statusCode
      body
    }
  }
`;

const enqueueReceiptsDownloadMutation = /* GraphQL */ `
  mutation EnqueueReceiptsDownload($input: EnqueueReceiptsDownloadInput!) {
    enqueueReceiptsDownload(input: $input) {
      statusCode
      body
    }
  }
`;

const removeProjectGroupMutation = /* GraphQL */ `
  mutation RemoveProjectGroup($input: RemoveProjectGroupInput!) {
    removeProjectGroup(input: $input) {
      statusCode
      body
    }
  }
`;

// Shared selection set for the two ops that return a ProjectGroup. The CDK type is a
// slim mirror — it carries no `@hasMany` connections — so `groupAdmins` is not
// selectable here. `v2UpdateProjectGroupAdmins` is the op that returns the links.
const projectGroupFields = /* GraphQL */ `
  id
  paidolId
  authPaidolId
  name
  groupProjects
  isMonthlyBudgetUnlimited
  spendGroupId
  monthlyBudget {
    value
    currency
  }
  deletedAt
  createdAt
  updatedAt
  __typename
`;

const v2CreateProjectGroupMutation = /* GraphQL */ `
  mutation V2CreateProjectGroup($input: V2CreateProjectGroupInput!) {
    v2CreateProjectGroup(input: $input) {
      ${projectGroupFields}
    }
  }
`;

const v2UpdateProjectGroupMutation = /* GraphQL */ `
  mutation V2UpdateProjectGroup($input: V2UpdateProjectGroupInput!) {
    v2UpdateProjectGroup(input: $input) {
      ${projectGroupFields}
    }
  }
`;

// SEE-301: the nested `paidolUser` is load-bearing, not decoration. The Amplify path
// finished by re-reading the links through listPaidolUserToProjectGroups, whose selection
// carried it; this mutation returns the links directly and the thunk splices the result
// straight onto the Redux entity as `groupAdmins.items`. Two consumers read through it —
// GroupSidesheet renders `paidolUser.user.first_name` and falls back to "Unknown" per
// chip, and mapToProjectGroup maps to `paidolUser` then filters, so ProjectGroupsPage
// sees the group as having no admins at all. Selecting scalars only made both wrong for
// the second between saving and the setTimeout refetch in utils.tsx re-upserting from a
// query that does select it.
//
// Kept as a TS comment rather than a `#` line inside the document: Prettier's GraphQL
// printer mishandles comments in these templates, which is what garbled three type
// declarations in schema.graphql (see c50c3635a).
const v2UpdateProjectGroupAdminsMutation = /* GraphQL */ `
  mutation V2UpdateProjectGroupAdmins($input: V2UpdateProjectGroupAdminsInput!) {
    v2UpdateProjectGroupAdmins(input: $input) {
      id
      paidolId
      paidolUserId
      projectGroupId
      createdAt
      updatedAt
      __typename
      paidolUser {
        id
        email
        user {
          id
          first_name
          last_name
        }
      }
    }
  }
`;

// SEE-301 Phase B — flex-coding search documents.
//
// Copied field-for-field from the live documents in `src/graphql/queries.ts`; CLAUDE.md
// bans new imports from `src/graphql/*`, so they are inlined rather than imported. The CDK
// payload types were built off these exact selection sets, so no query rewrite is needed.
//
// `__typename` is kept even though the CDK types omit it (codegen runs with
// `skipTypename`). It is a GraphQL meta-field, always selectable, and all three documents
// were confirmed accepted verbatim by the deployed API.
// `v2SearchFlexGlCodes` and `v2SearchFlexVendors` select an identical set — both entities
// are just a named row — so they share it. `v2SearchFlexCodes` differs and spells its own out.
const namedFlexEntityFields = /* GraphQL */ `
  items {
    id
    paidolId
    authPaidolId
    name
    flexCodeImportId
    createdAt
    updatedAt
    __typename
  }
  nextToken
  total
  __typename
`;

const v2SearchFlexGlCodesQuery = /* GraphQL */ `
  query V2SearchFlexGlCodes($input: SearchFlexCodingInput!) {
    v2SearchFlexGlCodes(input: $input) {
      ${namedFlexEntityFields}
    }
  }
`;

const v2SearchFlexCodesQuery = /* GraphQL */ `
  query V2SearchFlexCodes($input: SearchFlexCodingInput!) {
    v2SearchFlexCodes(input: $input) {
      items {
        id
        paidolId
        authPaidolId
        projectName
        projectCode
        isActive
        flexCodeImportId
        createdAt
        updatedAt
        __typename
      }
      nextToken
      total
      __typename
    }
  }
`;

const v2SearchFlexVendorsQuery = /* GraphQL */ `
  query V2SearchFlexVendors($input: SearchFlexCodingInput!) {
    v2SearchFlexVendors(input: $input) {
      ${namedFlexEntityFields}
    }
  }
`;

const v2SearchFlexCostCodesQuery = /* GraphQL */ `
  query V2SearchFlexCostCodes($input: SearchFlexCodingInput!) {
    v2SearchFlexCostCodes(input: $input) {
      items {
        id
        paidolId
        authPaidolId
        code
        flexCodeImportId
        createdAt
        updatedAt
        __typename
      }
      nextToken
      total
      __typename
    }
  }
`;

const v2SearchFlexCostTypesQuery = /* GraphQL */ `
  query V2SearchFlexCostTypes($input: SearchFlexCodingInput!) {
    v2SearchFlexCostTypes(input: $input) {
      ${namedFlexEntityFields}
    }
  }
`;

const v2GetTransactionFilterOptionsQuery = /* GraphQL */ `
  query V2GetTransactionFilterOptions($input: V2GetTransactionFilterOptionsInput!) {
    v2GetTransactionFilterOptions(input: $input) {
      paidolId
      authPaidolId
      last4
      userId {
        id
        label
        projectId
        __typename
      }
      merchantName {
        id
        label
        projectId
        __typename
      }
      merchantCategory {
        id
        label
        projectId
        __typename
      }
      cardGroup {
        id
        label
        projectId
        __typename
      }
      projectGroup {
        id
        label
        projectId
        __typename
      }
      receiptStatus {
        id
        label
        projectId
        __typename
      }
      highnoteTransactionStatus {
        id
        label
        projectId
        __typename
      }
      flexCodeTransactionStatus {
        id
        label
        projectId
        __typename
      }
      flexGlCodeIds {
        id
        label
        projectId
        __typename
      }
      flexCodeIds {
        id
        label
        projectId
        __typename
      }
      flexCostCodeIds {
        id
        label
        projectId
        __typename
      }
      flexCostTypeIds {
        id
        label
        projectId
        __typename
      }
      flexVendorIds {
        id
        label
        projectId
        __typename
      }
      agaveTransactionStatus {
        id
        label
        projectId
        __typename
      }
      agaveGlCodeIds {
        id
        label
        projectId
        __typename
      }
      agaveProjectIds {
        id
        label
        projectId
        __typename
      }
      agaveCostCodeIds {
        id
        label
        projectId
        __typename
      }
      agaveCostTypeIds {
        id
        label
        projectId
        __typename
      }
      agaveVendorIds {
        id
        label
        projectId
        __typename
      }
      agaveLocationIds {
        id
        label
        projectId
        __typename
      }
      agaveDepartmentIds {
        id
        label
        projectId
        __typename
      }
      agaveSubaccountIds {
        id
        label
        projectId
        __typename
      }
      agaveSubJobIds {
        id
        label
        projectId
        __typename
      }
      agaveEquipmentIds {
        id
        label
        projectId
        __typename
      }
      agaveEmCostCodeIds {
        id
        label
        projectId
        __typename
      }
      agaveEmCostTypeIds {
        id
        label
        projectId
        __typename
      }
      erpBillableItemIds {
        id
        label
        projectId
        __typename
      }
      statementPeriods {
        id
        label
        startDate
        endDate
        __typename
      }
      createdAt
      updatedAt
      __typename
    }
  }
`;

const v2GetProjectGroupQuery = /* GraphQL */ `
  query V2GetProjectGroup($input: GetProjectGroupInput!) {
    v2GetProjectGroup(input: $input) {
      projectGroup {
        id
        paidolId
        authPaidolId
        name
        groupProjects
        isMonthlyBudgetUnlimited
        spendGroupId
        monthlyBudget {
          value
          currency
        }
        monthlyBudgetSpend {
          items {
            yearAndMonth
            amount
          }
          nextToken
        }
        deletedAt
        createdAt
        updatedAt
        __typename
      }
      projects {
        id
        name
        __typename
      }
      groupAdmins {
        id
        paidolId
        paidolUserId
        projectGroupId
        createdAt
        updatedAt
        __typename
      }
      __typename
    }
  }
`;

const v2TransactionHealthCheckQuery = /* GraphQL */ `
  query V2TransactionHealthCheck($input: String) {
    v2TransactionHealthCheck(input: $input)
  }
`;

export interface UpdateTransactionInput {
  id: string;
  /** SEE-1843: one id per bulk action, so the admins get one self-approval email for it. */
  bulkOperationId?: string | null;

  note?: string | null;
  receiptPath?: string | null;
  receiptId?: string | null;

  fuelVehicleId?: string | null;
  fuelOdometerReading?: number | null;
  fuelGallons?: number | null;
  fuelCostPerGallon?: number | null;

  approveWithoutReceipts?: boolean | null;
  approveWithoutNotes?: boolean | null;

  jobCodeId?: string | null;

  flexCodeId?: string | null;
  flexGlCodeId?: string | null;
  flexCostCodeId?: string | null;
  flexCostTypeId?: string | null;
  flexVendorId?: string | null;

  agaveLocationId?: string | null;
  agaveDepartmentId?: string | null;
  agaveSubaccountId?: string | null;
  agaveEmployeeId?: string | null;
  agaveCostTypeId?: string | null;
  agaveCostCodeId?: string | null;
  agaveProjectId?: string | null;
  agaveGlCodeId?: string | null;
  agaveVendorId?: string | null;
  agaveSubJobId?: string | null;
  agaveEquipmentId?: string | null;
  agaveEmCostCodeId?: string | null;
  agaveEmCostTypeId?: string | null;
  agaveExpenseId?: string | null;
  agaveExpense?: CreateAgaveExpenseAPInvoiceInput | null;

  // SEE-974: the explicitly-selected transaction coding type (GL/PROJECT/EQUIPMENT).
  // Persisted so the type filter can match the SELECTED type even on uncoded rows.
  codingType?: string | null;

  procoreProjectId?: number | null;
  procoreWBSCodeId?: number | null;
  procoreProject?: GetProcoreProjectsPayloadInput | null;
  procoreWBSCode?: GetProcoreWBSCodesPayloadInput | null;

  flexCodeTransactionStatus?: FlexCodeTransactionStatus | null;
  workflowStatus?: WorkflowStatus | null;
  returnedStatus?: ReturnedStatus | null;
  erpApprovalStatus?: boolean | null;
  erpPostingStatus?: ERPPostingStatus | null;

  accountingDate?: string | null;
  erpBypassStatus?: boolean | null;

  // SEE-1135: per-transaction billable item (the billable signal). accountId=ITEMID from
  // the ERP item dropdown, plus a denormalized display name.
  erpBillableItemId?: string | null;
  erpBillableItemName?: string | null;
  // SEE-1390: per-transaction billable opt-out (false = post non-billable while
  // the item stays coded; auto-set true when an item lands).
  erpBillable?: boolean | null;
}

/** SEE-1604: what submitAllSplits returns. */
export interface SubmitAllSplitsResult {
  parentId: string;
  splitId: string;
  childIds: string[];
}

/** SEE-1874: what returnAllSplits returns. foldedBack is false when the returns landed
 * but the split could not be folded back; each split then stays returned on its own. */
export interface ReturnAllSplitsResult {
  parentId: string;
  returnedIds: string[];
  foldedBack: boolean;
}

export interface BasicHTTPResponse {
  statusCode: number;
  body?: string | null;
}

export interface RemoveProjectGroupInput {
  projectGroupId: string;
}

export interface ReturnTransactionToCardholderInput {
  transactionId: string;
  message: string;
}

export type TransactionReviewMessageAuthorRole = 'ADMIN' | 'SECONDARY_ADMIN' | 'GROUP_ADMIN';

export interface TransactionReviewMessage {
  id: string;
  transactionId: string;
  message: string;
  authorId: string;
  authorName: string;
  authorRole: TransactionReviewMessageAuthorRole;
  createdAt: string;
}

export interface WaitingOnEntry {
  kind: 'CARD_GROUP' | 'PROJECT_GROUP' | 'COMPANY';
  groupName: string | null;
  level: number | null;
  roles: string[] | null;
  /** Empty when there are more than five; `total` still counts them. */
  people: string[];
  total: number;
}

export interface TransactionWaitingOn {
  step: string | null;
  awaitingClearing: boolean;
  entries: WaitingOnEntry[];
}

export interface EnqueueTransactionsDownloadInput {
  paidolId: string;
  columns: string[];
  rangeOption: ExportDateRangeEnum;
  startDate?: string | null;
  endDate?: string | null;
  onlyMyTransactions?: boolean | null;
  // Administrator on a flex account: once the backend has built the file, the Approved
  // rows in it are marked EXPORTED.
  markAsExported?: boolean | null;
}

export interface MarkTransactionsExportedInput {
  paidolId: string;
  transactionIds: string[];
}

export interface MarkTransactionsExportedResult {
  exportedIds: string[];
  skippedIds: string[];
}

export interface EnqueueReceiptsDownloadInput {
  paidolId: string;
  rangeOption: ExportDateRangeEnum;
  startDate?: string | null;
  endDate?: string | null;
  filteredTransactionIds?: string[] | null;
  onlyMyTransactions?: boolean | null;
}

export interface EnqueuedDownloadResult {
  id: string;
  paidol_id: string;
  user_id: string;
  cron_status: string;
  createdAt: string;
}

const _AWSJSON_KEYS = ['agaveExpense', 'procoreProject', 'procoreWBSCode'] as const;

/**
 * SEE-1823: a `searchSpxTransactions` page whose items keep the Amplify
 * `HighnoteTransactionEvent` type. The transaction slice, the group and job-code pages and
 * the tables are typed against it. They used the Amplify `SearchSpxTransactionsPayload`
 * until R2 removed that type with the field.
 */
export type SpxTransactionsPage = Omit<SearchSpxTransactionsPayload, 'items'> & {
  items?: HighnoteTransactionEvent[] | null;
};

export class TransactionService {
  private readonly cdkAppSyncEndpoint: string;

  constructor() {
    this.cdkAppSyncEndpoint = process.env.REACT_APP_CDK_APPSYNC_ENDPOINT || '';

    if (!this.cdkAppSyncEndpoint) {
      // eslint-disable-next-line no-console
      console.warn('REACT_APP_CDK_APPSYNC_ENDPOINT is not configured');
    }
  }

  async updateTransaction(input: UpdateTransactionInput): Promise<Record<string, unknown>> {
    const payload = this.serialize(input);
    const result = await this.execute<{ updateTransaction: BasicHTTPResponse }>(
      updateTransactionMutation,
      { input: payload },
      'updateTransaction'
    );
    const response = result.updateTransaction;
    if (!response || response.statusCode == null) {
      throw new Error('updateTransaction returned no payload');
    }
    if (response.statusCode !== 200) {
      throw new Error(response.body || `updateTransaction failed (${response.statusCode})`);
    }
    if (!response.body) {
      return {};
    }
    return JSON.parse(response.body) as Record<string, unknown>;
  }

  async returnTransactionToCardholder(
    input: ReturnTransactionToCardholderInput
  ): Promise<TransactionReviewMessage> {
    const result = await this.execute<{ returnTransactionToCardholder: BasicHTTPResponse }>(
      returnTransactionToCardholderMutation,
      { input },
      'returnTransactionToCardholder'
    );
    const response = result.returnTransactionToCardholder;
    if (!response || response.statusCode !== 200 || !response.body) {
      throw new Error(response?.body || 'returnTransactionToCardholder failed');
    }
    return JSON.parse(response.body) as TransactionReviewMessage;
  }

  /**
   * SEE-1874: return every split of a split transaction (the parent's id) to the
   * cardholder, adding the message to each split's thread. The split folds back into
   * the parent as a returned draft. A refusal comes back as its message.
   */
  async returnAllSplits(input: ReturnTransactionToCardholderInput): Promise<ReturnAllSplitsResult> {
    const result = await this.execute<{ returnAllSplits: BasicHTTPResponse }>(
      returnAllSplitsMutation,
      { input },
      'returnAllSplits'
    );
    const response = result.returnAllSplits;
    if (!response || response.statusCode !== 200 || !response.body) {
      throw new Error(response?.body || 'returnAllSplits failed');
    }
    return JSON.parse(response.body) as ReturnAllSplitsResult;
  }

  /**
   * SEE-1604: turn the transaction's split draft into submitted, locked children.
   * The backend creates the children, submits each through the normal workflow,
   * locks their amounts, marks the parent and deletes the draft. A refusal (flag off,
   * not New, already split, draft missing or invalid) comes back as its message.
   */
  async submitAllSplits(transactionId: string): Promise<SubmitAllSplitsResult> {
    const result = await this.execute<{ submitAllSplits: BasicHTTPResponse }>(
      submitAllSplitsMutation,
      { input: { transactionId } },
      'submitAllSplits'
    );
    const response = result.submitAllSplits;
    if (!response || response.statusCode !== 200 || !response.body) {
      throw new Error(response?.body || 'submitAllSplits failed');
    }
    return JSON.parse(response.body) as SubmitAllSplitsResult;
  }

  async listTransactionReviewMessages(transactionId: string): Promise<TransactionReviewMessage[]> {
    const result = await this.execute<{ listTransactionReviewMessages: BasicHTTPResponse }>(
      listTransactionReviewMessagesQuery,
      { input: { transactionId } },
      'listTransactionReviewMessages'
    );
    const response = result.listTransactionReviewMessages;
    if (!response || response.statusCode !== 200 || !response.body) {
      throw new Error(response?.body || 'listTransactionReviewMessages failed');
    }
    return JSON.parse(response.body) as TransactionReviewMessage[];
  }

  async getTransactionWaitingOn(id: string): Promise<TransactionWaitingOn> {
    const result = await this.execute<{ getTransactionWaitingOn: BasicHTTPResponse }>(
      getTransactionWaitingOnQuery,
      { input: { id } },
      'getTransactionWaitingOn'
    );
    const response = result.getTransactionWaitingOn;
    if (!response || response.statusCode !== 200 || !response.body) {
      throw new Error(response?.body || 'getTransactionWaitingOn failed');
    }
    return JSON.parse(response.body) as TransactionWaitingOn;
  }

  async enqueueTransactionsDownload(
    input: EnqueueTransactionsDownloadInput
  ): Promise<EnqueuedDownloadResult> {
    const result = await this.execute<{ enqueueTransactionsDownload: BasicHTTPResponse }>(
      enqueueTransactionsDownloadMutation,
      { input },
      'enqueueTransactionsDownload'
    );
    return this.parseEnqueueResponse(result.enqueueTransactionsDownload, 'enqueueTransactionsDownload');
  }

  /**
   * Mark transactions the browser just wrote into a CSV as Exported.
   *
   * The current-view download is built client-side, so the server never sees that
   * file and the caller sends back the ids it contained. The server export marks its
   * own rows after upload and needs no equivalent call.
   */
  async markTransactionsExported(
    input: MarkTransactionsExportedInput
  ): Promise<MarkTransactionsExportedResult> {
    const result = await this.execute<{ markTransactionsExported: BasicHTTPResponse }>(
      markTransactionsExportedMutation,
      { input },
      'markTransactionsExported'
    );
    const payload = result.markTransactionsExported;
    if (!payload || payload.statusCode !== 200 || !payload.body) {
      throw new Error(payload?.body || 'markTransactionsExported failed');
    }
    return JSON.parse(payload.body) as MarkTransactionsExportedResult;
  }

  async enqueueReceiptsDownload(input: EnqueueReceiptsDownloadInput): Promise<EnqueuedDownloadResult> {
    const result = await this.execute<{ enqueueReceiptsDownload: BasicHTTPResponse }>(
      enqueueReceiptsDownloadMutation,
      { input },
      'enqueueReceiptsDownload'
    );
    return this.parseEnqueueResponse(result.enqueueReceiptsDownload, 'enqueueReceiptsDownload');
  }

  /**
   * Reads the cached transaction filter options for a paidol.
   *
   * Returns `null` when the cache row does not exist yet — that is a legitimate
   * empty result. An authorization denial (`assert_paidol_member`) arrives as a
   * GraphQL error and is thrown by `execute`, so callers can tell the two apart.
   */
  async v2GetTransactionFilterOptions(input: {
    paidolId: string;
  }): Promise<V2TransactionFilterOptionsPayload | null> {
    const result = await this.execute<{
      v2GetTransactionFilterOptions: V2TransactionFilterOptionsPayload | null;
    }>(v2GetTransactionFilterOptionsQuery, { input }, 'v2GetTransactionFilterOptions');
    return result.v2GetTransactionFilterOptions ?? null;
  }

  /**
   * Reads a project group with its projects and group admins.
   *
   * `group_id` is snake_case: it is the live wire contract on both the Amplify and
   * CDK APIs, and the handler reads `event.input["group_id"]`.
   */
  async v2GetProjectGroup(input: GetProjectGroupInput): Promise<GetProjectGroupPayload | null> {
    const result = await this.execute<{ v2GetProjectGroup: GetProjectGroupPayload | null }>(
      v2GetProjectGroupQuery,
      { input },
      'v2GetProjectGroup'
    );
    return result.v2GetProjectGroup ?? null;
  }

  /**
   * Deletes a project group.
   *
   * The CDK resolver asserts `assert_admin`, so a non-admin caller gets a GraphQL
   * error (thrown by `execute`) rather than a silent no-op. A non-200 payload also
   * throws, so callers cannot mistake a refused delete for a successful one.
   */
  async removeProjectGroup(input: RemoveProjectGroupInput): Promise<BasicHTTPResponse> {
    const result = await this.execute<{ removeProjectGroup: BasicHTTPResponse }>(
      removeProjectGroupMutation,
      { input },
      'removeProjectGroup'
    );
    const response = result.removeProjectGroup;
    if (!response || response.statusCode == null) {
      throw new Error('removeProjectGroup returned no payload');
    }
    if (response.statusCode !== 200) {
      throw new Error(response.body || `removeProjectGroup failed (${response.statusCode})`);
    }
    return response;
  }

  /**
   * Creates a project group.
   *
   * `authPaidolId` is deliberately absent from the input — the server derives it from
   * `paidolId`. Accepting it from a caller would let an admin of tenant A mint a group
   * that tenant B can read and edit.
   *
   * The CDK resolver asserts the role matrix (Primary and Secondary admins only), so an
   * unauthorized caller gets the generic GraphQL error thrown by `execute`. There is no
   * status code to inspect — this op returns a `ProjectGroup`, not an envelope.
   */
  async v2CreateProjectGroup(input: V2CreateProjectGroupInput): Promise<CdkProjectGroup> {
    const result = await this.execute<{ v2CreateProjectGroup: CdkProjectGroup | null }>(
      v2CreateProjectGroupMutation,
      { input },
      'v2CreateProjectGroup'
    );
    if (!result.v2CreateProjectGroup) {
      throw new Error('v2CreateProjectGroup returned no payload');
    }
    return result.v2CreateProjectGroup;
  }

  /**
   * Updates a project group. Tenant comes from the stored row, so the input carries
   * neither `paidolId` nor `authPaidolId`.
   */
  async v2UpdateProjectGroup(input: V2UpdateProjectGroupInput): Promise<CdkProjectGroup> {
    const result = await this.execute<{ v2UpdateProjectGroup: CdkProjectGroup | null }>(
      v2UpdateProjectGroupMutation,
      { input },
      'v2UpdateProjectGroup'
    );
    if (!result.v2UpdateProjectGroup) {
      throw new Error('v2UpdateProjectGroup returned no payload');
    }
    return result.v2UpdateProjectGroup;
  }

  /**
   * Sets the group's admins to the desired set and returns the resulting links.
   *
   * Send the whole set, not a delta — the server diffs it against the stored links,
   * prunes duplicates, and returns the full resulting set. The paginated read, the
   * add/remove diff and the fan-out of create/delete calls that used to live in
   * `projectGroupSlice` all happen server-side under one assertion now.
   */
  async v2UpdateProjectGroupAdmins(
    input: V2UpdateProjectGroupAdminsInput
  ): Promise<CdkPaidolUserToProjectGroup[]> {
    const result = await this.execute<{
      v2UpdateProjectGroupAdmins: (CdkPaidolUserToProjectGroup | null)[] | null;
    }>(v2UpdateProjectGroupAdminsMutation, { input }, 'v2UpdateProjectGroupAdmins');
    // A group with no admins is a legitimate empty list, so `null` collapses to `[]`
    // rather than throwing — unlike the two ops above, where a null group is an error.
    return (result.v2UpdateProjectGroupAdmins ?? []).filter(
      (link): link is CdkPaidolUserToProjectGroup => link != null
    );
  }

  /**
   * SEE-301 Phase B — flex-coding searches.
   *
   * All three return `null` only when the payload itself is absent. An empty `items` array
   * is a legitimate empty result and comes back as a populated payload with `total: 0`.
   * A backend failure or an `assert_paidol_member` denial arrives as a GraphQL error and is
   * thrown by `execute`, so callers can tell "no results" from "the search failed" — which
   * the Amplify path could not do, because its dispatcher returned failures as data.
   *
   * Callers must therefore handle rejection. Reducers that only set `loading: true` on
   * pending will hang once repointed; see SEE-1392.
   */
  async v2SearchFlexGlCodes(input: CdkSearchFlexCodingInput): Promise<SearchFlexGlCodePayload | null> {
    const result = await this.execute<{
      v2SearchFlexGlCodes: SearchFlexGlCodePayload | null;
    }>(v2SearchFlexGlCodesQuery, { input }, 'v2SearchFlexGlCodes');
    return result.v2SearchFlexGlCodes ?? null;
  }

  async v2SearchFlexCodes(input: CdkSearchFlexCodingInput): Promise<SearchFlexCodesPayload | null> {
    const result = await this.execute<{
      v2SearchFlexCodes: SearchFlexCodesPayload | null;
    }>(v2SearchFlexCodesQuery, { input }, 'v2SearchFlexCodes');
    return result.v2SearchFlexCodes ?? null;
  }

  async v2SearchFlexVendors(input: CdkSearchFlexCodingInput): Promise<SearchFlexVendorsPayload | null> {
    const result = await this.execute<{
      v2SearchFlexVendors: SearchFlexVendorsPayload | null;
    }>(v2SearchFlexVendorsQuery, { input }, 'v2SearchFlexVendors');
    return result.v2SearchFlexVendors ?? null;
  }

  async v2SearchFlexCostCodes(input: CdkSearchFlexCodingInput): Promise<SearchFlexCostCodesPayload | null> {
    const result = await this.execute<{
      v2SearchFlexCostCodes: SearchFlexCostCodesPayload | null;
    }>(v2SearchFlexCostCodesQuery, { input }, 'v2SearchFlexCostCodes');
    return result.v2SearchFlexCostCodes ?? null;
  }

  async v2SearchFlexCostTypes(input: CdkSearchFlexCodingInput): Promise<SearchFlexCostTypesPayload | null> {
    const result = await this.execute<{
      v2SearchFlexCostTypes: SearchFlexCostTypesPayload | null;
    }>(v2SearchFlexCostTypesQuery, { input }, 'v2SearchFlexCostTypes');
    return result.v2SearchFlexCostTypes ?? null;
  }

  /**
   * SEE-301 Phase B. The highest-risk op in the ticket (crux-3): the transaction list
   * itself, on the app's default landing tab, reached from three call sites.
   *
   * Note the shape difference from the Amplify path this replaces. `graphqlRequest`
   * resolved with a `GraphQLResult` and callers reached into `result.data`; the CDK
   * dispatcher raises on failure, so this either returns a payload or throws. Callers
   * get the payload directly.
   */
  async searchSpxTransactions(
    input: SearchSpxTransactionsInput
  ): Promise<SearchSpxTransactionsPayload | null> {
    const result = await this.execute<{
      searchSpxTransactions: SearchSpxTransactionsPayload | null;
    }>(SearchHighnoteTransactions, { input }, 'searchSpxTransactions');
    return result.searchSpxTransactions ?? null;
  }

  /** SEE-1232 rows i/j — slim HighnoteTransactionEvent reads (see the documents above). */
  async getHighnoteTransactionEventPostingStatus(id: string): Promise<TransactionEventPostingStatus | null> {
    const result = await this.execute<{ getHighnoteTransactionEvent: TransactionEventPostingStatus | null }>(
      getHighnoteTransactionEventPostingStatusQuery,
      { id },
      'getHighnoteTransactionEvent'
    );
    return result.getHighnoteTransactionEvent;
  }

  async listHighnoteTransactionEventsByPaidolId(variables: {
    paidolId: string;
    limit?: number;
    nextToken?: string | null;
  }): Promise<TransactionEventsPage<{ id: string }>> {
    const result = await this.execute<{
      listHighnoteTransactionEventsByPaidolId: TransactionEventsPage<{ id: string }> | null;
    }>(listHighnoteTransactionEventsByPaidolIdQuery, variables, 'listHighnoteTransactionEventsByPaidolId');
    return {
      items: result.listHighnoteTransactionEventsByPaidolId?.items ?? [],
      nextToken: result.listHighnoteTransactionEventsByPaidolId?.nextToken ?? null,
    };
  }

  async listHighnoteTransactionEventsByTransactionId(variables: {
    transactionId: string;
    sortDirection?: 'ASC' | 'DESC';
    limit?: number;
  }): Promise<TransactionEventsPage<{ id: string; eventDate?: string | null; status?: string | null }>> {
    const result = await this.execute<{
      listHighnoteTransactionEventsByTransactionId: TransactionEventsPage<{
        id: string;
        eventDate?: string | null;
        status?: string | null;
      }> | null;
    }>(
      listHighnoteTransactionEventsByTransactionIdQuery,
      variables,
      'listHighnoteTransactionEventsByTransactionId'
    );
    return {
      items: result.listHighnoteTransactionEventsByTransactionId?.items ?? [],
      nextToken: result.listHighnoteTransactionEventsByTransactionId?.nextToken ?? null,
    };
  }

  /**
   * SEE-1233 rows a–d — Flex Builder (see the documents above). Results are cast to the
   * `API.ts` types the slice and pages already use, as the slice cast `API.graphql` results
   * before. On the wire the shapes are identical: the field names match, and no document
   * selects `__typename`.
   */
  async listFlexFieldsByPaidolId(
    variables: QueryFlexFieldsByPaidolIdArgs
  ): Promise<{ items: FlexField[]; nextToken: string | null }> {
    const result = await this.execute<{
      flexFieldsByPaidolId: { items: FlexField[]; nextToken?: string | null } | null;
    }>(flexFieldsByPaidolIdQuery, variables, 'flexFieldsByPaidolId');
    return {
      items: result.flexFieldsByPaidolId?.items ?? [],
      nextToken: result.flexFieldsByPaidolId?.nextToken ?? null,
    };
  }

  async listFlexFieldValuesByFlexFieldId(
    variables: QueryFlexFieldValuesByFlexFieldIdArgs
  ): Promise<{ items: FlexFieldValue[]; nextToken: string | null }> {
    const result = await this.execute<{
      flexFieldValuesByFlexFieldId: { items: FlexFieldValue[]; nextToken?: string | null } | null;
    }>(flexFieldValuesByFlexFieldIdQuery, variables, 'flexFieldValuesByFlexFieldId');
    return {
      items: result.flexFieldValuesByFlexFieldId?.items ?? [],
      nextToken: result.flexFieldValuesByFlexFieldId?.nextToken ?? null,
    };
  }

  /**
   * `source` stays a `string`, matching the `createFlexField` thunk's argument. The server
   * derives `authPaidolId` from `paidolId`.
   */
  async createFlexField(
    input: Omit<CdkCreateFlexFieldInput, 'source'> & { source: string }
  ): Promise<FlexField> {
    const result = await this.execute<{ createFlexField: FlexField | null }>(
      createFlexFieldMutation,
      { input },
      'createFlexField'
    );
    // The upload page reads `.id` off this to create the upload, so fail here rather
    // than hand back a null.
    if (!result.createFlexField) {
      throw new Error('createFlexField returned no payload');
    }
    return result.createFlexField;
  }

  /**
   * The server derives `authPaidolId` and sets `status` to `PROCESSING`, and it accepts
   * only a `storageFileKey` issued by `v2CreateFileUploadUrl` for this company.
   */
  async createFlexFieldUpload(input: CdkCreateFlexFieldUploadInput): Promise<FlexFieldUpload | null> {
    const result = await this.execute<{ createFlexFieldUpload: FlexFieldUpload | null }>(
      createFlexFieldUploadMutation,
      { input },
      'createFlexFieldUpload'
    );
    return result.createFlexFieldUpload;
  }

  async v2TransactionHealthCheck(input: string): Promise<string> {
    const result = await this.execute<{ v2TransactionHealthCheck: string }>(
      v2TransactionHealthCheckQuery,
      { input },
      'v2TransactionHealthCheck'
    );
    return result.v2TransactionHealthCheck;
  }

  private parseEnqueueResponse(
    response: BasicHTTPResponse | undefined,
    operationName: string
  ): EnqueuedDownloadResult {
    if (!response || response.statusCode == null) {
      throw new Error(`${operationName} returned no payload`);
    }
    if (response.statusCode !== 200) {
      throw new Error(response.body || `${operationName} failed (${response.statusCode})`);
    }
    if (!response.body) {
      throw new Error(`${operationName} returned an empty body`);
    }
    return JSON.parse(response.body) as EnqueuedDownloadResult;
  }

  private serialize(input: UpdateTransactionInput): Record<string, unknown> {
    const out: Record<string, unknown> = { ...input };
    for (const key of _AWSJSON_KEYS) {
      const value = out[key];
      if (value !== undefined && value !== null) {
        out[key] = JSON.stringify(value);
      }
    }
    return out;
  }

  private async execute<T>(
    query: string,
    variables: Record<string, unknown>,
    operationName: string,
    authMode: AuthMode = 'cognito'
  ): Promise<T> {
    if (!this.cdkAppSyncEndpoint) {
      captureException(new Error('CDK AppSync endpoint is not configured'), {
        tags: { area: 'transaction', type: 'config' },
        extra: { envVar: 'REACT_APP_CDK_APPSYNC_ENDPOINT' },
      });
      throw new Error('Service is currently unavailable. Please contact support.');
    }

    try {
      const body = JSON.stringify({ query, variables });
      let response: Response;

      if (authMode === 'iam') {
        response = await this.executeWithIamAuth(body);
      } else {
        const session = await Auth.currentSession();
        const jwt = session.getIdToken().getJwtToken();
        response = await fetchWithTransportRetry(this.cdkAppSyncEndpoint, {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            Authorization: jwt,
          },
          body,
        });
      }

      const json = (await response.json()) as GraphQLResult<T> & {
        errors?: Array<{ message: string }>;
      };

      reportCdkRefusal(json?.errors, { authMode, operationName });

      if (!response.ok) {
        const message = json?.errors?.map((e) => e.message).join(', ') || `HTTP ${response.status}`;
        throw new Error(message);
      }

      if (json.errors && json.errors.length > 0) {
        const errorMessage = json.errors.map((e) => e.message).join(', ');
        throw new Error(`GraphQL errors: ${errorMessage}`);
      }

      if (!json.data) {
        throw new Error('No data returned from operation');
      }

      return json.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Failed to execute ${operationName}:`, error);

      if (error instanceof Error) {
        captureException(error, {
          // Transport failures (connection died in transit, already retried) are
          // tagged apart from HTTP/GraphQL rejections so they're separable in Sentry.
          tags: {
            area: 'transaction',
            type: isNetworkTransportError(error) ? 'network-transport' : 'graphql',
          },
          extra: { operation: operationName, endpoint: this.cdkAppSyncEndpoint },
        });
        throw error;
      }

      throw new Error('An unexpected error occurred. Please try again.');
    }
  }

  private async executeWithIamAuth(body: string): Promise<Response> {
    const credentials = await Auth.currentCredentials();
    const { accessKeyId, secretAccessKey, sessionToken } = credentials;

    const url = new URL(this.cdkAppSyncEndpoint);
    const regionMatch = url.hostname.match(/\.appsync-api\.([^.]+)\./);
    const region = regionMatch?.[1] ?? 'us-east-2';

    const request = {
      method: 'POST',
      url: this.cdkAppSyncEndpoint,
      headers: {
        'content-type': 'application/json',
        host: url.host,
      },
      data: body,
    };

    const signedRequest = Signer.sign(
      request,
      { access_key: accessKeyId, secret_key: secretAccessKey, session_token: sessionToken },
      { region, service: 'appsync' }
    );

    return fetchWithTransportRetry(this.cdkAppSyncEndpoint, {
      method: 'POST',
      headers: signedRequest.headers,
      body,
    });
  }
}

export const transactionService = new TransactionService();
